import i18next from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import { useCallback } from "react";

export type Language = "en" | "ja" | "zh";
export const LANGUAGE_STORAGE_KEY = "ezgames.language";
export const languages = [
  { code: "en", label: "English", short: "EN", flag: "gb", htmlLang: "en", locale: "en-US" },
  { code: "ja", label: "日本語", short: "JA", flag: "jp", htmlLang: "ja", locale: "ja-JP" },
  { code: "zh", label: "简体中文", short: "ZH", flag: "cn", htmlLang: "zh-Hans", locale: "zh-CN" },
] as const;

type Messages = Record<string, string>;
const englishFiles = import.meta.glob<Messages>("./locales/en/*.json", { eager: true, import: "default" });
const translatedFiles = import.meta.glob<Messages>("./locales/{ja,zh}/*.json", { import: "default" });
const english: Record<string, Messages> = {};
const owners = new Map<string, string>();
for (const [path, messages] of Object.entries(englishFiles)) {
  const namespace = path.split("/").pop()!.replace(".json", "");
  english[namespace] = messages;
  for (const key of Object.keys(messages)) if (!owners.has(key)) owners.set(key, namespace);
}

export const i18n = i18next.createInstance();
void i18n.use(initReactI18next).init({
  lng: "en",
  fallbackLng: "en",
  supportedLngs: ["en", "ja", "zh"],
  load: "currentOnly",
  resources: { en: english },
  ns: Object.keys(english),
  defaultNS: "Navbar",
  keySeparator: false,
  nsSeparator: false,
  initAsync: false,
  interpolation: { escapeValue: false }, // React escapes the rendered text.
  react: { useSuspense: false },
});

const loaded = new Set<Language>(["en"]);
const pending = new Map<Language, Promise<void>>();
async function loadLanguage(language: Language) {
  if (loaded.has(language)) return;
  if (!pending.has(language)) {
    const request = Promise.all(Object.entries(translatedFiles)
      .filter(([path]) => path.includes(`/${language}/`))
      .map(async ([path, load]) => {
        const namespace = path.split("/").pop()!.replace(".json", "");
        i18n.addResourceBundle(language, namespace, await load(), true, true);
      })).then(() => { loaded.add(language); }).finally(() => { pending.delete(language); });
    pending.set(language, request);
  }
  await pending.get(language);
}

export function currentLanguage(): Language {
  return languages.some(({ code }) => code === i18n.language) ? i18n.language as Language : "en";
}

function applyDocumentLanguage(language: Language) {
  document.documentElement.lang = languages.find(({ code }) => code === language)!.htmlLang;
  document.documentElement.dir = "ltr";
}

export async function changeLanguage(language: Language) {
  await loadLanguage(language);
  await i18n.changeLanguage(language);
  applyDocumentLanguage(language);
  try { localStorage.setItem(LANGUAGE_STORAGE_KEY, language); } catch { /* Storage can be blocked. */ }
}

export async function initializeI18n() {
  let saved: string | null = null;
  try { saved = localStorage.getItem(LANGUAGE_STORAGE_KEY); } catch { /* English remains available. */ }
  const language = languages.find(({ code }) => code === saved)?.code ?? "en";
  try { await changeLanguage(language); } catch { applyDocumentLanguage("en"); }
}

/** Translate display copy only. IDs, routes, filter values and user input stay in English/original form. */
export function translate<T>(value: T, values?: Record<string, unknown>, namespace?: string): T {
  if (typeof value !== "string" || !value.trim()) return value;
  const key = value.replace(/\s+/g, " ").trim();
  const owner = namespace && Object.hasOwn(english[namespace] ?? {}, key) ? namespace : owners.get(key);
  if (!owner) return value;
  const result = i18n.t(key, { ...values, ns: owner, defaultValue: key });
  const before = /^\s/.test(value) ? " " : "";
  const after = /\s$/.test(value) ? " " : "";
  return (before + result + after) as T;
}

export function useI18n(namespace: string) {
  const { i18n: instance } = useTranslation(namespace, { i18n });
  const language = currentLanguage();
  const t = useCallback(<T,>(value: T, values?: Record<string, unknown>): T =>
    translate(value, values, namespace), [namespace, instance.language]);
  return { t, language, locale: languages.find(({ code }) => code === language)!.locale };
}

export function formatDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(languages.find(({ code }) => code === currentLanguage())!.locale,
    { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" }).format(date);
}
